// @ts-nocheck — TODO[type-debt]: 5 type mismatch reali (Combobox onChange / SaldoCardProps / Dispatch). Fix in sessione dedicata.
/**
 * Pagina punto vendita — interfaccia POS a tre colonne.
 *
 * Colonna SX: ricerca articoli magazzino + inserimento manuale riga.
 * Colonna centrale: barra scontrini in sospeso + tabella righe scontrino attivo.
 * Colonna DX: card totali, card pagamenti, bottoni azione.
 *
 * Supporta scontrini multipli aperti nello stesso turno (scontrini in sospeso).
 * Il multi-cassiere è gestito dal backend via operatore_id dal token JWT.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { CreditCard, Package, PanelLeftClose, PanelLeftOpen, Plus, Search, ShoppingCart, Trash2, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/* ─────────────────────────── Costanti nominate (DEC-018) ─────────────────────────── */

const METODI_PAGAMENTO = [
  { value: 'contanti', label: 'Contanti', codice_tender_rt: 1 },
  { value: 'carta_credito', label: 'Carta di credito', codice_tender_rt: 2 },
  { value: 'carta_debito', label: 'Bancomat', codice_tender_rt: 3 },
  { value: 'assegno', label: 'Assegno', codice_tender_rt: 4 },
  { value: 'ticket', label: 'Ticket / Buono pasto', codice_tender_rt: 5 },
  { value: 'credito', label: 'Credito cliente', codice_tender_rt: 6 },
  { value: 'buono', label: 'Buono / Voucher', codice_tender_rt: 7 },
] as const;

const ALIQUOTE_IVA = [22, 10, 5, 4, 0] as const;
const DEFAULT_REPARTO_RT = 1;
const SEARCH_DEBOUNCE_MS = 300;

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface TurnoInfo {
  id: string;
  stato: string;
}

interface ScontrinoInfo {
  id: string;
  year: number;
  stato: string;
  subtotale: string;
  totale: string;
  sconto_totale: string;
}

interface RigaInfo {
  id: string;
  descrizione: string;
  quantita: string;
  prezzo_unitario: string;
  importo_riga: string;
  soglia_applicata: string;
}

interface PagamentoInfo {
  id: string;
  metodo: string;
  importo: string;
  importo_ricevuto: string | null;
  resto: string | null;
}

interface ArticoloRicerca {
  id: string;
  sku_interno: string;
  descrizione: string;
  giacenza: number;
}

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Wrappa Numeric PostgreSQL (stringa JSON) in number e formatta come valuta. */
const _currencyFmt = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatCurrency = (value: string | number): string =>
  _currencyFmt.format(Number(value));

const sogliaVariant = (soglia: string) => {
  switch (soglia) {
    case 'minimo':
      return 'secondary' as const;
    case 'forzato':
      return 'destructive' as const;
    default:
      return 'outline' as const;
  }
};

/* ─────────────────────────── Componente ─────────────────────────── */

const PuntoVendita: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  /* ── State: turno ── */
  const [turno, setTurno] = useState<TurnoInfo | null>(null);
  const [turnoLoading, setTurnoLoading] = useState(true);

  /* ── State: scontrini in sospeso ── */
  const [scontriniAperti, setScontriniAperti] = useState<ScontrinoInfo[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);

  /* ── State: righe e pagamenti dello scontrino attivo ── */
  const [righe, setRighe] = useState<RigaInfo[]>([]);
  const [pagamenti, setPagamenti] = useState<PagamentoInfo[]>([]);

  /* ── State: ricerca articoli ── */
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ArticoloRicerca[]>([]);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initDone = useRef(false);
  const prezzoInputRef = useRef<HTMLInputElement>(null);

  /* ── State: form inserimento manuale ── */
  const [manDescrizione, setManDescrizione] = useState('');
  const [manPrezzo, setManPrezzo] = useState('');
  const [manQta, setManQta] = useState('1');
  const [manRepartoRT, setManRepartoRT] = useState(String(DEFAULT_REPARTO_RT));
  const [manAliquotaIVA, setManAliquotaIVA] = useState(String(ALIQUOTE_IVA[0]));

  /* ── State: dialog pagamento ── */
  const [pagamentoOpen, setPagamentoOpen] = useState(false);
  const [pagMetodo, setPagMetodo] = useState(METODI_PAGAMENTO[0].value);
  const [pagImporto, setPagImporto] = useState('');
  const [pagRicevuto, setPagRicevuto] = useState('');

  /* ── State: modale articolo occasionale ── */
  const [occOpen, setOccOpen] = useState(false);
  const [occDescrizione, setOccDescrizione] = useState('');
  const [occCostoAcquisto, setOccCostoAcquisto] = useState('');
  const [occPrezzoVendita, setOccPrezzoVendita] = useState('');
  const [occQta, setOccQta] = useState('1');
  const [occIVA, setOccIVA] = useState(String(ALIQUOTE_IVA[0]));
  const [occRepartoRT, setOccRepartoRT] = useState(String(DEFAULT_REPARTO_RT));
  const [occForzMotivo, setOccForzMotivo] = useState('');
  const [occSoglie, setOccSoglie] = useState<{
    prezzo_minimo: string;
    prezzo_consigliato: string;
    prezzo_massimo: string;
  } | null>(null);
  const occSoglieTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── State: generici ── */
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pannelloSx, setPannelloSx] = useState(true);

  /* ── Derived ── */
  const scontrinoAttivo: ScontrinoInfo | null =
    scontriniAperti.length > 0 ? scontriniAperti[activeIdx] ?? null : null;

  const totalePagato = pagamenti.reduce(
    (acc, p) => acc + Number(p.importo),
    0,
  );
  const totale = scontrinoAttivo ? Number(scontrinoAttivo.totale) : 0;
  const rimanente = Math.max(0, totale - totalePagato);

  const metodoCorrente = METODI_PAGAMENTO.find((m) => m.value === pagMetodo);
  const isContanti = pagMetodo === 'contanti';
  const restoContanti = isContanti
    ? Math.max(0, Number(pagRicevuto || 0) - Number(pagImporto || 0))
    : 0;

  /* ─────────────────────── Fetch helpers ─────────────────────── */

  /** Cerca turno aperto. */
  const fetchTurno = useCallback(async () => {
    setTurnoLoading(true);
    try {
      const res = await fetch('/api/v1/cassa/turni?stato=aperto&limit=1', {
        headers: authHeadersGet(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: TurnoInfo[] = await res.json();
      setTurno(data.length > 0 ? data[0] : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento turno');
    } finally {
      setTurnoLoading(false);
    }
  }, []);

  /** Carica tutti gli scontrini aperti del turno. */
  const fetchScontriniAperti = useCallback(
    async (turnoId: string) => {
      try {
        const params = new URLSearchParams({
          year: String(currentYear),
          stato: 'aperto',
          turno_id: turnoId,
        });
        const res = await fetch(`/api/v1/cassa/scontrini?${params}`, {
          headers: authHeadersGet(),
        });
        if (!res.ok) throw new Error(`Errore ${res.status}`);
        const data: ScontrinoInfo[] = await res.json();
        setScontriniAperti(data);
        return data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore caricamento scontrini');
        return [];
      }
    },
    [currentYear],
  );

  /** Crea un nuovo scontrino nel turno corrente. */
  const creaScontrino = useCallback(
    async (turnoId: string): Promise<ScontrinoInfo | null> => {
      try {
        const res = await fetch('/api/v1/cassa/scontrini', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ turno_id: turnoId }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail ?? `Errore ${res.status}`);
        }
        return (await res.json()) as ScontrinoInfo;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore creazione scontrino');
        return null;
      }
    },
    [],
  );

  /** Ricarica lo scontrino singolo (per totali aggiornati). */
  const fetchScontrino = useCallback(
    async (scontrinoId: string, year: number): Promise<ScontrinoInfo | null> => {
      try {
        const res = await fetch(
          `/api/v1/cassa/scontrini/${scontrinoId}?year=${year}`,
          { headers: authHeadersGet() },
        );
        if (!res.ok) throw new Error(`Errore ${res.status}`);
        return (await res.json()) as ScontrinoInfo;
      } catch {
        return null;
      }
    },
    [],
  );

  /** Carica le righe dello scontrino. */
  const fetchRighe = useCallback(async (scontrinoId: string, year: number) => {
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoId}/righe?year=${year}`,
        { headers: authHeadersGet() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setRighe(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento righe');
    }
  }, []);

  /** Carica i pagamenti dello scontrino. */
  const fetchPagamenti = useCallback(async (scontrinoId: string, year: number) => {
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoId}/pagamenti?year=${year}`,
        { headers: authHeadersGet() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setPagamenti(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento pagamenti');
    }
  }, []);

  /** Ricarica scontrino + righe + pagamenti. */
  const refreshScontrino = useCallback(
    async (scontrinoId: string, year: number) => {
      const updated = await fetchScontrino(scontrinoId, year);
      if (updated) {
        setScontriniAperti((prev) =>
          prev.map((s) => (s.id === scontrinoId ? updated : s)),
        );
      }
      await Promise.all([
        fetchRighe(scontrinoId, year),
        fetchPagamenti(scontrinoId, year),
      ]);
    },
    [fetchScontrino, fetchRighe, fetchPagamenti],
  );

  /* ─────────────────────── Azioni ─────────────────────── */

  /** Ricerca articoli magazzino con debounce. */
  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/v1/magazzino/articoli?q=${encodeURIComponent(value.trim())}`,
          { headers: authHeadersGet() },
        );
        if (!res.ok) return;
        setSearchResults(await res.json());
      } catch {
        /* ignora errori di ricerca */
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  /** Click su articolo dalla ricerca: pre-compila il form manuale e focus sul prezzo. */
  const selezionaArticolo = (art: ArticoloRicerca) => {
    setManDescrizione(art.descrizione);
    setManPrezzo('');
    setManQta('1');
    setSearchQuery('');
    setSearchResults([]);
    /* Il modulo prezzi (7) non e ancora implementato: focus sul campo prezzo */
    requestAnimationFrame(() => prezzoInputRef.current?.focus());
  };

  /** Aggiunge una riga allo scontrino attivo. */
  const aggiungiRiga = async () => {
    if (!scontrinoAttivo || !manDescrizione || !manPrezzo) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/righe?year=${scontrinoAttivo.year}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            descrizione: manDescrizione,
            reparto_rt: Number(manRepartoRT),
            prezzo_unitario: manPrezzo,
            aliquota_iva: Number(manAliquotaIVA),
            quantita: manQta,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      setManDescrizione('');
      setManPrezzo('');
      setManQta('1');
      await refreshScontrino(scontrinoAttivo.id, scontrinoAttivo.year);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore aggiunta riga');
    } finally {
      setSubmitting(false);
    }
  };

  /** Rimuove una riga dallo scontrino. */
  const rimuoviRiga = async (rigaId: string) => {
    if (!scontrinoAttivo) return;
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/righe/${rigaId}?year=${scontrinoAttivo.year}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      await refreshScontrino(scontrinoAttivo.id, scontrinoAttivo.year);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore rimozione riga');
    }
  };

  /** Crea un nuovo scontrino (parcheggia il corrente). */
  const nuovoScontrino = async () => {
    if (!turno) return;
    const nuovo = await creaScontrino(turno.id);
    if (nuovo) {
      setScontriniAperti((prev) => [...prev, nuovo]);
      setActiveIdx(scontriniAperti.length);
    }
  };

  /** Cambia scontrino attivo. */
  const switchScontrino = (idx: number) => {
    setActiveIdx(idx);
  };

  /** Apre la dialog pagamento con importo pre-compilato. */
  const apriPagamento = () => {
    setPagMetodo(METODI_PAGAMENTO[0].value);
    setPagImporto(rimanente.toFixed(2));
    setPagRicevuto('');
    setPagamentoOpen(true);
  };

  /** Conferma un pagamento. */
  const confermaPagamento = async () => {
    if (!scontrinoAttivo || !metodoCorrente) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        metodo: metodoCorrente.value,
        codice_tender_rt: metodoCorrente.codice_tender_rt,
        importo: pagImporto,
      };
      if (isContanti && pagRicevuto) {
        body.importo_ricevuto = pagRicevuto;
        body.resto = restoContanti.toFixed(2);
      }
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/pagamenti?year=${scontrinoAttivo.year}`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      await refreshScontrino(scontrinoAttivo.id, scontrinoAttivo.year);

      /* Ricalcola rimanente dopo il refresh */
      const updatedScontrino = await fetchScontrino(scontrinoAttivo.id, scontrinoAttivo.year);
      const pagRes = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/pagamenti?year=${scontrinoAttivo.year}`,
        { headers: authHeadersGet() },
      );
      const updatedPagamenti: PagamentoInfo[] = pagRes.ok ? await pagRes.json() : [];
      const newTotalePagato = updatedPagamenti.reduce((acc, p) => acc + Number(p.importo), 0);
      const newTotale = updatedScontrino ? Number(updatedScontrino.totale) : 0;
      const newRimanente = Math.max(0, newTotale - newTotalePagato);

      if (newRimanente <= 0) {
        /* Pagamento completato: prepara per chiusura */
        setPagImporto('0.00');
      } else {
        /* Ancora da pagare: aggiorna importo */
        setPagImporto(newRimanente.toFixed(2));
        setPagRicevuto('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  /** Chiude lo scontrino (RT non connesso nel prototipo). */
  const chiudiScontrino = async () => {
    if (!scontrinoAttivo) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/chiudi?year=${scontrinoAttivo.year}`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({}) },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      setPagamentoOpen(false);
      /* Rimuovi lo scontrino chiuso dalla lista */
      const nuovaLista = scontriniAperti.filter((s) => s.id !== scontrinoAttivo.id);
      setScontriniAperti(nuovaLista);
      setActiveIdx(Math.min(activeIdx, Math.max(0, nuovaLista.length - 1)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore chiusura scontrino');
    } finally {
      setSubmitting(false);
    }
  };

  /** Annulla lo scontrino. */
  const annullaScontrino = async () => {
    if (!scontrinoAttivo) return;
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/annulla?year=${scontrinoAttivo.year}`,
        { method: 'POST', headers: authHeaders() },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      const nuovaLista = scontriniAperti.filter((s) => s.id !== scontrinoAttivo.id);
      setScontriniAperti(nuovaLista);
      setActiveIdx(Math.min(activeIdx, Math.max(0, nuovaLista.length - 1)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore annullamento');
    }
  };

  /* ─────────────────────── Effects ─────────────────────── */

  /** On mount: cerca turno aperto. */
  useEffect(() => {
    void fetchTurno();
  }, [fetchTurno]);

  /** Quando il turno è trovato: carica scontrini aperti o crea uno nuovo. */
  useEffect(() => {
    if (!turno || initDone.current) return;
    initDone.current = true;
    const init = async () => {
      const aperti = await fetchScontriniAperti(turno.id);
      const vuoleNuovo = searchParams.get('nuovo') === '1';
      if (aperti.length === 0 || vuoleNuovo) {
        const nuovo = await creaScontrino(turno.id);
        if (nuovo) {
          const nuovaLista = [...aperti, nuovo];
          setScontriniAperti(nuovaLista);
          setActiveIdx(nuovaLista.length - 1);
        }
      } else {
        setActiveIdx(0);
      }
      /* Pulisci ?nuovo=1 dall'URL per evitare ri-creazione su navigazione */
      if (vuoleNuovo) {
        navigate('/cassa/vendita', { replace: true });
      }
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turno]);

  /** Quando cambia lo scontrino attivo: ricarica righe e pagamenti. */
  useEffect(() => {
    if (scontrinoAttivo) {
      void fetchRighe(scontrinoAttivo.id, scontrinoAttivo.year);
      void fetchPagamenti(scontrinoAttivo.id, scontrinoAttivo.year);
    } else {
      setRighe([]);
      setPagamenti([]);
    }
  }, [scontrinoAttivo?.id, fetchRighe, fetchPagamenti]);

  /* ─────────────────────── Articolo occasionale ─────────────────────── */

  /** Debounce: chiama soglie-al-volo quando cambia costo acquisto. */
  const onOccCostoChange = (value: string) => {
    setOccCostoAcquisto(value);
    if (occSoglieTimeout.current) clearTimeout(occSoglieTimeout.current);
    if (!value || Number(value) <= 0) {
      setOccSoglie(null);
      return;
    }
    occSoglieTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/v1/prezzi/listino/soglie-al-volo', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ costo_acquisto: value }),
        });
        if (res.ok) setOccSoglie(await res.json());
      } catch {
        /* ignora errori — soglie non bloccanti */
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  /** Badge colore per il prezzo di vendita rispetto alle soglie. */
  const occPrezzoBadge = (): { label: string; className: string } | null => {
    if (!occSoglie || !occPrezzoVendita) return null;
    const pv = Number(occPrezzoVendita);
    const min = Number(occSoglie.prezzo_minimo);
    const cons = Number(occSoglie.prezzo_consigliato);
    if (pv >= cons)
      return { label: 'Sopra consigliato', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    if (pv >= min)
      return { label: 'Tra minimo e consigliato', className: 'bg-accent/10 text-accent' };
    return { label: 'Sotto minimo', className: 'bg-destructive/10 text-destructive' };
  };

  const resetOccasionale = () => {
    setOccDescrizione('');
    setOccCostoAcquisto('');
    setOccPrezzoVendita('');
    setOccQta('1');
    setOccIVA(String(ALIQUOTE_IVA[0]));
    setOccRepartoRT(String(DEFAULT_REPARTO_RT));
    setOccForzMotivo('');
    setOccSoglie(null);
  };

  const confermaOccasionale = async () => {
    if (!scontrinoAttivo || !occDescrizione || !occCostoAcquisto || !occPrezzoVendita) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        descrizione: occDescrizione,
        iva_codice: Number(occIVA),
        costo_acquisto: occCostoAcquisto,
        prezzo_vendita: occPrezzoVendita,
        quantita: occQta,
        reparto_rt_id: Number(occRepartoRT),
      };
      if (occForzMotivo) body.forzatura_motivo = occForzMotivo;
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoAttivo.id}/righe-occasionale?year=${scontrinoAttivo.year}`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setOccOpen(false);
      resetOccasionale();
      void fetchRighe(scontrinoAttivo.id, scontrinoAttivo.year);
      // Ricarica scontrino per aggiornare totali
      if (turno) void fetchScontriniAperti(turno.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore articolo occasionale');
    } finally {
      setSubmitting(false);
    }
  };

  /* ─────────────────────── Render: nessun turno ─────────────────────── */

  if (turnoLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Caricamento turno…</p>
      </div>
    );
  }

  if (!turno) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <ShoppingCart className="h-12 w-12 text-muted-foreground" />
        <p className="text-lg text-muted-foreground">
          Nessun turno aperto
        </p>
        <p className="text-sm text-muted-foreground">
          Apri un turno dalla pagina Turni per iniziare a vendere.
        </p>
        <Button onClick={() => navigate('/cassa/turni')}>
          Vai a Turni
        </Button>
      </div>
    );
  }

  /* ─────────────────────── Render: POS ─────────────────────── */

  return (
    <div className="relative flex h-full flex-col overflow-hidden p-4">
      {/* ═══════ HEADER ═══════ */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPannelloSx(!pannelloSx)}
            title={pannelloSx ? 'Chiudi pannello articoli' : 'Apri pannello articoli'}
          >
            {pannelloSx ? (
              <PanelLeftClose className="h-5 w-5" />
            ) : (
              <PanelLeftOpen className="h-5 w-5" />
            )}
          </Button>
          <h1 className="font-outfit text-xl font-semibold text-neutral-dark dark:text-neutral-light">
            Punto vendita
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Barra scontrini in sospeso */}
          <div className="flex items-center gap-1 overflow-x-auto">
            {scontriniAperti.map((s: ScontrinoInfo, idx: number) => (
              <Button
                key={s.id}
                variant={idx === activeIdx ? 'default' : 'outline'}
                size="sm"
                className="flex-shrink-0"
                onClick={() => switchScontrino(idx)}
              >
                #{idx + 1}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="flex-shrink-0" onClick={nuovoScontrino}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Separator orientation="vertical" className="mx-1 h-6" />
          <Button variant="outline" size="sm" onClick={() => navigate('/cassa')}>
            Dashboard
          </Button>
        </div>
      </div>

      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

      {/* ═══════ CORPO: pannello SX (overlay) + centro + destra ═══════ */}
      <div className="flex min-h-0 flex-1 gap-3">
        {/* ── PANNELLO SINISTRO: ricerca + inserimento manuale ── */}
        {pannelloSx && (
          <div className="flex w-64 flex-shrink-0 flex-col gap-3 overflow-y-auto lg:w-72 xl:w-80">
            {/* Ricerca articoli */}
            <Card>
              <CardHeader className="px-3 pb-2 pt-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Search className="h-4 w-4" />
                  Cerca articolo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 px-3 pb-3">
                <Input
                  placeholder="SKU o descrizione…"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
                {searchResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                    {searchResults.map((art: ArticoloRicerca) => (
                      <button
                        key={art.id}
                        type="button"
                        className="flex w-full items-center justify-between px-2 py-1.5 text-left text-sm hover:bg-muted/50"
                        onClick={() => selezionaArticolo(art)}
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">{art.descrizione}</p>
                          <p className="text-xs text-muted-foreground">{art.sku_interno}</p>
                        </div>
                        <span className="ml-2 flex-shrink-0 text-xs text-muted-foreground">
                          {art.giacenza}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bottone articolo occasionale */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { resetOccasionale(); setOccOpen(true); }}
              disabled={!scontrinoAttivo}
            >
              <Package className="h-4 w-4 mr-1" />
              Articolo occasionale
            </Button>

            {/* Inserimento manuale */}
            <Card>
              <CardHeader className="px-3 pb-2 pt-3">
                <CardTitle className="text-sm">Inserimento manuale</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 px-3 pb-3">
                <div>
                  <Label className="text-xs">Descrizione</Label>
                  <Input
                    value={manDescrizione}
                    onChange={(e) => setManDescrizione(e.target.value)}
                    placeholder="Nome articolo"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Prezzo</Label>
                    <Input
                      ref={prezzoInputRef}
                      type="number"
                      step="0.01"
                      min="0"
                      value={manPrezzo}
                      onChange={(e) => setManPrezzo(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Qt&agrave;</Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={manQta}
                      onChange={(e) => setManQta(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Rep. RT</Label>
                    <Input
                      type="number"
                      min="1"
                      max="40"
                      value={manRepartoRT}
                      onChange={(e) => setManRepartoRT(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">IVA %</Label>
                    <Select value={manAliquotaIVA} onValueChange={setManAliquotaIVA}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALIQUOTE_IVA.map((a) => (
                          <SelectItem key={a} value={String(a)}>
                            {a}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="default"
                  className="w-full py-3 font-semibold"
                  onClick={aggiungiRiga}
                  disabled={submitting || !manDescrizione || !manPrezzo || !scontrinoAttivo}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Aggiungi riga
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── COLONNA CENTRALE: tabella righe ── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {scontrinoAttivo ? (
            <Card className="flex flex-1 flex-col overflow-hidden">
              <CardContent className="flex-1 overflow-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="w-14 text-right">Qt&agrave;</TableHead>
                      <TableHead className="w-20 text-right">Prezzo</TableHead>
                      <TableHead className="w-20 text-right">Totale</TableHead>
                      <TableHead className="w-16">Soglia</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {righe.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-sm text-muted-foreground"
                        >
                          Nessuna riga — aggiungi articoli
                        </TableCell>
                      </TableRow>
                    )}
                    {righe.map((r: RigaInfo) => (
                      <TableRow key={r.id}>
                        <TableCell className="max-w-[200px] truncate">{r.descrizione}</TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(r.quantita)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(r.prezzo_unitario)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(r.importo_riga)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={sogliaVariant(r.soglia_applicata)}>
                            {r.soglia_applicata}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => rimuoviRiga(r.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="flex flex-1 items-center justify-center">
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Nessuno scontrino aperto. Clicca &ldquo;+&rdquo; per crearne uno.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── COLONNA DESTRA: totali + pagamenti + azioni ── */}
        <div className="flex w-48 flex-shrink-0 flex-col lg:w-56 xl:w-64">
          {scontrinoAttivo ? (
            <>
              {/* Card scrollabili */}
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                {/* Totali */}
                <Card>
                  <CardHeader className="px-3 pb-2 pt-3">
                    <CardTitle className="text-sm">Totali</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 px-3 pb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Subtotale</span>
                      <span className="font-mono">
                        {formatCurrency(scontrinoAttivo.subtotale)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Sconti</span>
                      <span className="font-mono text-destructive">
                        -{formatCurrency(scontrinoAttivo.sconto_totale)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-base font-semibold">
                      <span>Totale</span>
                      <span className="font-mono">
                        {formatCurrency(scontrinoAttivo.totale)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Pagamenti */}
                <Card>
                  <CardHeader className="px-3 pb-2 pt-3">
                    <CardTitle className="text-sm">Pagamenti</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 px-3 pb-3">
                    {pagamenti.length === 0 && (
                      <p className="text-xs text-muted-foreground">Nessun pagamento</p>
                    )}
                    {pagamenti.map((p: PagamentoInfo) => (
                      <div key={p.id} className="flex justify-between text-xs">
                        <span className="text-muted-foreground capitalize">{p.metodo}</span>
                        <span className="font-mono">{formatCurrency(p.importo)}</span>
                      </div>
                    ))}
                    <Separator />
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Pagato</span>
                      <span className="font-mono">{formatCurrency(totalePagato)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold">
                      <span>Rimane</span>
                      <span className="font-mono">{formatCurrency(rimanente)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bottoni azione — sempre fissi in fondo, mai scrollabili */}
              <div className="flex flex-shrink-0 flex-col gap-2 pt-3">
                <Button
                  variant="accent"
                  className="w-full py-6 text-base font-bold shadow-md"
                  onClick={apriPagamento}
                  disabled={totale <= 0}
                >
                  <CreditCard className="mr-2 h-5 w-5" />
                  Pagamento
                </Button>
                <Button
                  variant="destructive"
                  className="w-full py-4 font-semibold"
                  style={{ backgroundColor: 'var(--color-destructive)', color: '#fff' }}
                  onClick={annullaScontrino}
                >
                  <XCircle className="mr-2 h-5 w-5" />
                  Annulla scontrino
                </Button>
              </div>
            </>
          ) : (
            <Card className="flex flex-1 items-center justify-center">
              <CardContent className="p-3">
                <p className="text-center text-xs text-muted-foreground">
                  Nessuno scontrino selezionato
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ═══════ DIALOG ARTICOLO OCCASIONALE ═══════ */}
      <Dialog open={occOpen} onOpenChange={setOccOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Articolo occasionale</DialogTitle>
            <DialogDescription>
              Inserisci i dati dell&apos;articolo non a catalogo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Descrizione</Label>
              <Input
                value={occDescrizione}
                onChange={(e) => setOccDescrizione(e.target.value)}
                placeholder="Nome articolo"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Costo acquisto</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={occCostoAcquisto}
                  onChange={(e) => onOccCostoChange(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Prezzo vendita</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={occPrezzoVendita}
                  onChange={(e) => setOccPrezzoVendita(e.target.value)}
                  placeholder="0.00"
                />
                {occPrezzoBadge() && (
                  <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${occPrezzoBadge()!.className}`}>
                    {occPrezzoBadge()!.label}
                  </span>
                )}
              </div>
            </div>
            {/* Soglie calcolate */}
            {occSoglie && (
              <div className="rounded-md bg-muted p-2 text-xs space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Minimo</span>
                  <span className="font-mono">{formatCurrency(occSoglie.prezzo_minimo)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Consigliato</span>
                  <span className="font-mono">{formatCurrency(occSoglie.prezzo_consigliato)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Massimo</span>
                  <span className="font-mono">{formatCurrency(occSoglie.prezzo_massimo)}</span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Quantità</Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={occQta}
                  onChange={(e) => setOccQta(e.target.value)}
                />
              </div>
              <div>
                <Label>IVA %</Label>
                <Select value={occIVA} onValueChange={setOccIVA}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALIQUOTE_IVA.map((a) => (
                      <SelectItem key={a} value={String(a)}>
                        {a}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rep. RT</Label>
                <Input
                  type="number"
                  min="1"
                  max="40"
                  value={occRepartoRT}
                  onChange={(e) => setOccRepartoRT(e.target.value)}
                />
              </div>
            </div>
            {/* Motivazione forzatura se sotto minimo */}
            {occSoglie && Number(occPrezzoVendita) < Number(occSoglie.prezzo_minimo) && Number(occPrezzoVendita) > 0 && (
              <div>
                <Label className="text-destructive">Motivazione forzatura (obbligatoria)</Label>
                <Input
                  value={occForzMotivo}
                  onChange={(e) => setOccForzMotivo(e.target.value)}
                  placeholder="Motivazione vendita sotto prezzo minimo…"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOccOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={confermaOccasionale}
              disabled={
                submitting ||
                !occDescrizione ||
                !occCostoAcquisto ||
                !occPrezzoVendita ||
                (occSoglie != null &&
                  Number(occPrezzoVendita) < Number(occSoglie.prezzo_minimo) &&
                  Number(occPrezzoVendita) > 0 &&
                  !occForzMotivo)
              }
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ DIALOG PAGAMENTO ═══════ */}
      <Dialog open={pagamentoOpen} onOpenChange={setPagamentoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pagamento</DialogTitle>
            <DialogDescription>
              Totale: {formatCurrency(totale)} — Rimane: {formatCurrency(rimanente)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Metodo</Label>
              <Select value={pagMetodo} onValueChange={setPagMetodo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METODI_PAGAMENTO.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Importo</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pagImporto}
                onChange={(e) => setPagImporto(e.target.value)}
              />
            </div>

            {isContanti && (
              <>
                <div>
                  <Label>Ricevuto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pagRicevuto}
                    onChange={(e) => setPagRicevuto(e.target.value)}
                  />
                </div>
                {Number(pagRicevuto) > 0 && (
                  <div className="flex justify-between rounded-md bg-muted p-3 text-sm font-semibold">
                    <span>Resto</span>
                    <span className="font-mono">{formatCurrency(restoContanti)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="default"
              className="font-semibold"
              onClick={confermaPagamento}
              disabled={submitting || !pagImporto || Number(pagImporto) <= 0}
            >
              Conferma pagamento
            </Button>
            {rimanente <= 0 && pagamenti.length > 0 && (
              <Button
                variant="accent"
                className="font-bold"
                onClick={chiudiScontrino}
                disabled={submitting}
              >
                Chiudi scontrino
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PuntoVendita;
